export class StreamTextUtils {
  extractLeadingJSON(buffer) {
    let i = 0;
    while (i < buffer.length && /\s/.test(buffer[i])) i++;
    if (i >= buffer.length || buffer[i] !== "{") return null;

    let depth = 0;
    let inString = false;
    let escape = false;
    const start = i;

    for (; i < buffer.length; i++) {
      const ch = buffer[i];

      if (inString) {
        if (escape) {
          escape = false;
        } else if (ch === "\\") {
          escape = true;
        } else if (ch === '"') {
          inString = false;
        }
        continue;
      }

      if (ch === '"') {
        inString = true;
      } else if (ch === "{") {
        depth++;
      } else if (ch === "}") {
        depth--;
        if (depth === 0) {
          const jsonStr = buffer.slice(start, i + 1);
          try {
            const obj = JSON.parse(jsonStr);
            const rest = buffer.slice(i + 1);
            return { obj, rest };
          } catch {
            return null;
          }
        }
      }
    }

    return null;
  }

  /**
   * Try to parse a trailing JSON object that ends at the end of the string.
   * Useful when the backend appends a raw `{ "sources": [...] }` blob.
   */
  extractTrailingSourcesJSON(text) {
    if (!text) return null;
    const s = String(text);
    const trimmed = s.trimEnd();
    if (!trimmed.endsWith("}")) return null;

    // Fast path: find a top-level `"sources"` key near the end.
    const sourcesKeyIndex = trimmed.lastIndexOf('"sources"');
    if (sourcesKeyIndex >= 0) {
      const startBrace = trimmed.lastIndexOf("{", sourcesKeyIndex);
      if (startBrace >= 0) {
        const slice = trimmed.slice(startBrace);
        const extracted = this.extractLeadingJSON(slice);
        if (
          extracted &&
          extracted.rest.trim().length === 0 &&
          Array.isArray(extracted.obj?.sources)
        ) {
          return { obj: extracted.obj, startIndex: startBrace };
        }
      }
    }

    // Fallback: scan a bounded tail for a JSON object ending the string.
    const minIndex = Math.max(0, trimmed.length - 250000);
    for (let i = trimmed.lastIndexOf("{"); i >= minIndex && i !== -1; ) {
      const slice = trimmed.slice(i);
      const extracted = this.extractLeadingJSON(slice);
      if (
        extracted &&
        extracted.rest.trim().length === 0 &&
        Array.isArray(extracted.obj?.sources)
      ) {
        return { obj: extracted.obj, startIndex: i };
      }
      i = trimmed.lastIndexOf("{", i - 1);
    }

    return null;
  }

  normalizeApiText(s) {
    if (!s) return "";
    let text = s;
    try {
      text = text.replace(/\\u[\dA-Fa-f]{4}/g, (match) => {
        return String.fromCharCode(parseInt(match.replace(/\\u/g, ""), 16));
      });
    } catch {
      // ignore
    }

    return text
      .replace(/\\n/g, "\n")
      .replace(/\n{3,}/g, "\n\n")
      .replace(/[ ]{2,}/g, " ")
      .replace(/\s+\n/g, "\n")
      .replace(/\n\s+/g, "\n")
      .trim();
  }

  indexSourcesByContextId(rawSources) {
    const sources = Array.isArray(rawSources) ? rawSources : [];
    const indexed = [];
    for (const s of sources) {
      if (typeof s?.context_id === "number") indexed[s.context_id] = s;
      else indexed.push(s);
    }
    return indexed;
  }

  /**
   * Planner responses sometimes append a raw "sources: {...}" JSON blob to the
   * visible text. We want to hide that from the answer UI while still parsing
   * it from the stream buffer for context.
   */
  stripTrailingSourcesSection(text) {
    if (!text) return "";
    const s = String(text);
    const idx = s.lastIndexOf("sources:");
    if (idx < 0) return s;

    // Only strip when it looks like a trailing metadata block (near the end).
    const nearEnd = idx >= Math.max(0, s.length - 8000);
    const precededByNewline = idx === 0 ? true : s[idx - 1] === "\n";
    if (!nearEnd && !precededByNewline) return s;

    return s.slice(0, idx).trimEnd();
  }

  stripTrailingSourcesPayload(text) {
    if (!text) return "";
    const s = String(text);

    // 1) Legacy planner format: "... \nsources: { ... }"
    const legacyStripped = this.stripTrailingSourcesSection(s);
    if (legacyStripped !== s) return legacyStripped;

    // 2) Newer format: raw trailing JSON object containing { "sources": [...] }
    const extracted = this.extractTrailingSourcesJSON(s);
    if (!extracted) return s;

    const precededByWhitespace =
      extracted.startIndex === 0
        ? true
        : /\s/.test(s[extracted.startIndex - 1]);
    if (!precededByWhitespace) return s;

    return s.slice(0, extracted.startIndex).trimEnd();
  }

  /**
   * Extract the latest available sources payload from mixed stream text.
   * Supports:
   * 1) Leading JSON: `{ "sources": [...] }`
   * 2) Inline planner/deep format: `sources: { "sources": [...] }`
   * 3) Trailing raw JSON object containing `"sources"`.
   */
  extractLatestSourcesFromText(raw) {
    const text = typeof raw === "string" ? raw : "";
    if (!text) return null;

    let latestObj = null;

    // Leading JSON block.
    const leading = this.extractLeadingJSON(text);
    if (leading && Array.isArray(leading.obj?.sources)) {
      latestObj = leading.obj;
    }

    // Scan all `sources:` markers and keep the last valid JSON payload.
    let scanFrom = 0;
    while (scanFrom < text.length) {
      const idx = text.indexOf("sources:", scanFrom);
      if (idx < 0) break;
      const after = text.slice(idx + "sources:".length).trimStart();
      const extracted = this.extractLeadingJSON(after);
      if (extracted && Array.isArray(extracted.obj?.sources)) {
        latestObj = extracted.obj;
      }
      scanFrom = idx + "sources:".length;
    }

    // Trailing raw JSON `{ "sources": [...] }` if present.
    const trailing = this.extractTrailingSourcesJSON(text);
    if (trailing && Array.isArray(trailing.obj?.sources)) {
      latestObj = trailing.obj;
    }

    return latestObj;
  }

  /**
   * Extract and combine sources from all source payloads present in text.
   * Dedup priority:
   * 1) context_id
   * 2) context_idx
   * 3) chunk_idx
   * 4) docs_id + context
   */
  extractCombinedSourcesFromText(raw) {
    const text = typeof raw === "string" ? raw : "";
    if (!text) return [];

    const buckets = [];
    const pushSources = (obj) => {
      if (Array.isArray(obj?.sources) && obj.sources.length > 0) {
        buckets.push(obj.sources);
      }
    };

    // Leading JSON block.
    pushSources(this.extractLeadingJSON(text)?.obj);

    // All inline `sources:` payloads.
    let scanFrom = 0;
    while (scanFrom < text.length) {
      const idx = text.indexOf("sources:", scanFrom);
      if (idx < 0) break;
      const after = text.slice(idx + "sources:".length).trimStart();
      pushSources(this.extractLeadingJSON(after)?.obj);
      scanFrom = idx + "sources:".length;
    }

    // Trailing raw JSON block.
    pushSources(this.extractTrailingSourcesJSON(text)?.obj);

    const merged = [];
    const seen = new Set();
    for (const list of buckets) {
      for (const source of list) {
        const key =
          source?.context_id !== undefined && source?.context_id !== null
            ? `context_id:${source.context_id}`
            : source?.context_idx !== undefined && source?.context_idx !== null
              ? `context_idx:${source.context_idx}`
              : source?.chunk_idx !== undefined && source?.chunk_idx !== null
                ? `chunk_idx:${source.chunk_idx}`
                : `docs_context:${String(source?.docs_id || "")}:${String(source?.context || "")}`;
        if (seen.has(key)) continue;
        seen.add(key);
        merged.push(source);
      }
    }

    return merged;
  }
}
